const BASE = '/api';

async function req(url, options = {}) {
  try {
    const r = await fetch(url, { credentials: 'include', ...options });
    // If the session expired fire a global event so App.jsx resets auth state.
    if (r.status === 401 && !url.includes('/auth/')) {
      window.dispatchEvent(new Event('session-expired'));
    }
    // Parse as text first — r.json() throws on non-JSON 401/500 error pages.
    const text = await r.text();
    try {
      return JSON.parse(text);
    } catch {
      return { error: text || 'Unexpected server response.' };
    }
  } catch {
    return { error: 'Network error. Please check your connection.' };
  }
}

export async function getMe() {
  return req(BASE + '/auth/me');
}
export async function login(username, password) {
  return req(BASE + '/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });
}
export async function logout() {
  await req(BASE + '/auth/logout', { method: 'POST' });
}
export async function validateInvite(token) {
  return req(BASE + '/auth/invite/' + token);
}
export async function register(token, username, password) {
  return req(BASE + '/auth/register', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, username, password }),
  });
}
export async function changePassword(currentPassword, newPassword) {
  return req(BASE + '/auth/change-password', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ currentPassword, newPassword }),
  });
}
export async function deleteAccount(password, confirm) {
  return req(BASE + '/auth/delete-account', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password, confirm }),
  });
}
export async function loadData() {
  return req(BASE + '/data');
}
export async function saveData(data, extraOptions = {}, version) {
  return req(BASE + '/data', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(Number.isInteger(version) ? { data, version } : { data }),
    ...extraOptions,
  });
}
export async function completeOnboarding() {
  return req(BASE + '/data/complete-onboarding', { method: 'POST' });
}
export async function sendCoachMessage(message, history) {
  return req(BASE + '/coach', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ message, history }),
  });
}
export async function parseNlq(text, context = {}) {
  return req(BASE + '/nlq', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text, context }),
  });
}
export async function runSetupWizard(text, existing = null, files = []) {
  return req(BASE + '/setup-wizard', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text, existing, files }),
  });
}
export async function getUsers() {
  return req(BASE + '/data/users');
}
export async function deleteUser(id) {
  return req(BASE + '/data/users/' + id, { method: 'DELETE' });
}
export async function createInvite() {
  return req(BASE + '/data/invites', { method: 'POST' });
}
export async function getInvites() {
  return req(BASE + '/data/invites');
}
export async function deleteInvite(id) {
  return req(BASE + '/data/invites/' + id, { method: 'DELETE' });
}
export async function generateResetToken(userId) {
  return req(BASE + '/data/reset-token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId }),
  });
}
export async function validateResetToken(token) {
  return req(BASE + '/auth/reset/' + token);
}
export async function resetPassword(token, newPassword) {
  return req(BASE + '/auth/reset-password', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, newPassword }),
  });
}
export async function importTransactions(payload) {
  return req(BASE + '/import', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}
export const getImportStatus = () => req(BASE + '/import/status');

// ── Social ────────────────────────────────────────────────────────────────────
const J = (b) => ({ headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(b) });

export const getSocialProfile = () => req(BASE + '/social/profile');
export const updateSocialProfile = (d) => req(BASE + '/social/profile', { method: 'PUT', ...J(d) });
export const syncSocialStats = (d) => req(BASE + '/social/sync-stats', { method: 'POST', ...J(d) });

export const getLeaderboard = () => req(BASE + '/social/leaderboard');
export const getFriendsLeaderboard = () => req(BASE + '/social/leaderboard/friends');

export const getFriends = () => req(BASE + '/social/friends');
export const sendFriendRequest = (username) => req(BASE + '/social/friends/request', { method: 'POST', ...J({ username }) });
export const acceptFriendRequest = (id) => req(BASE + '/social/friends/accept/' + id, { method: 'POST' });
export const removeFriend = (id) => req(BASE + '/social/friends/' + id, { method: 'DELETE' });

export const getMyClan = () => req(BASE + '/social/clans/mine');
export const getPublicClans = () => req(BASE + '/social/clans/public');
export const createClan = (d) => req(BASE + '/social/clans', { method: 'POST', ...J(d) });
export const joinClan = (inviteCode) => req(BASE + '/social/clans/join', { method: 'POST', ...J({ inviteCode }) });
export const getClanDetails = (id) => req(BASE + '/social/clans/' + id);
export const leaveClan = (id) => req(BASE + '/social/clans/' + id + '/leave', { method: 'DELETE' });
export const deleteClan = (id) => req(BASE + '/social/clans/' + id, { method: 'DELETE' });

export const getFeed = (page = 0) => req(BASE + '/social/feed?page=' + page);
export const getClanFeed = (id) => req(BASE + '/social/feed/clan/' + id);
export const createPost = (d) => req(BASE + '/social/posts', { method: 'POST', ...J(d) });
export const deletePost = (id) => req(BASE + '/social/posts/' + id, { method: 'DELETE' });
export const editPost = (id, content) => req(BASE + '/social/posts/' + id, { method: 'PATCH', ...J({ content }) });
export const reportPost = (id, reason) => req(BASE + '/social/posts/' + id + '/report', { method: 'POST', ...J({ reason }) });
export const getPostReports = () => req(BASE + '/social/reports');
export const resolvePostReport = (id) => req(BASE + '/social/reports/' + id + '/resolve', { method: 'POST' });

export const submitFeedback = (type, message, meta) => req(BASE + '/data/feedback', { method: 'POST', ...J({ type, message, meta }) });
export const getAdminFeedback = () => req(BASE + '/data/feedback');
export const resolveFeedback = (id) => req(BASE + '/data/feedback/' + id + '/resolve', { method: 'POST' });
export const likePost = (id) => req(BASE + '/social/posts/' + id + '/like', { method: 'POST' });
export const getComments = (id) => req(BASE + '/social/posts/' + id + '/comments');
export const addComment = (postId, content) => req(BASE + '/social/posts/' + postId + '/comments', { method: 'POST', ...J({ content }) });

export const getBattles = () => req(BASE + '/social/battles');
export const createBattle = (d) => req(BASE + '/social/battles', { method: 'POST', ...J(d) });
export const acceptBattle = (id) => req(BASE + '/social/battles/' + id + '/accept', { method: 'POST' });
export const declineBattle = (id) => req(BASE + '/social/battles/' + id + '/decline', { method: 'POST' });

export const getClanBattles = () => req(BASE + '/social/battles/clans');
export const createClanBattle = (d) => req(BASE + '/social/battles/clans', { method: 'POST', ...J(d) });
export const acceptClanBattle = (id) => req(BASE + '/social/battles/clans/' + id + '/accept', { method: 'POST' });

export const reactToPost = (postId, reaction) => req(BASE + '/social/posts/' + postId + '/react', { method: 'POST', ...J({ reaction }) });
export const getPostReactions = (postId) => req(BASE + '/social/posts/' + postId + '/reactions');

export const getNotifications = () => req(BASE + '/social/notifications');
export const getNotificationCount = () => req(BASE + '/social/notifications/count');
export const markNotificationsRead = () => req(BASE + '/social/notifications/read', { method: 'POST' });

export const getFriendActivities = () => req(BASE + '/social/activities/friends');
export const logActivity = (type, data) => req(BASE + '/social/activities', { method: 'POST', ...J({ type, data }) });

export const getConversations = () => req(BASE + '/social/dm/conversations');
export const getDMs = (userId) => req(BASE + '/social/dm/' + userId);
export const sendDM = (userId, content) => req(BASE + '/social/dm/' + userId, { method: 'POST', ...J({ content }) });

export const getChallenge = () => req(BASE + '/social/challenge');
export const joinChallenge = () => req(BASE + '/social/challenge/join', { method: 'POST' });
export const syncChallengeScore = (score) => req(BASE + '/social/challenge/sync', { method: 'POST', ...J({ score }) });

export const votePost = (postId, vote) => req(BASE + '/social/posts/' + postId + '/vote', { method: 'POST', ...J({ vote }) });

export const getAccountability = () => req(BASE + '/social/accountability');
export const requestAccountabilityBuddy = (username) => req(BASE + '/social/accountability/request', { method: 'POST', ...J({ username }) });
export const acceptAccountabilityBuddy = (userId) => req(BASE + '/social/accountability/accept/' + userId, { method: 'POST' });
export const removeAccountabilityBuddy = () => req(BASE + '/social/accountability', { method: 'DELETE' });

export const getSeasons = () => req(BASE + '/social/seasons');
export const claimReferralXP = () => req(BASE + '/social/referral/claim', { method: 'POST' });

export const getBoss = () => req(BASE + '/social/boss');
export const hitBoss = (damage) => req(BASE + '/social/boss/hit', { method: 'POST', ...J({ damage }) });

export const getPublicGoals = () => req(BASE + '/social/goals/public');
export const getMyPublicGoals = () => req(BASE + '/social/goals/mine');
export const createPublicGoal = (d) => req(BASE + '/social/goals', { method: 'POST', ...J(d) });
export const updatePublicGoal = (id, currentPct) => req(BASE + '/social/goals/' + id, { method: 'PUT', ...J({ currentPct }) });
export const deletePublicGoal = (id) => req(BASE + '/social/goals/' + id, { method: 'DELETE' });


export const getPushVapidKey = () => req(BASE + '/push/vapid-key');
export const subscribePush = (sub) => req(BASE + '/push/subscribe', { method: 'POST', ...J(sub) });
export const unsubscribePush = (endpoint) => req(BASE + '/push/unsubscribe', { method: 'DELETE', ...J({ endpoint }) });
